import { Button, cn } from '@kanbu/chatbot-ui';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { MinusIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { AppSettings } from '@/constants/AppSettings';
import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { useCampaign } from '@/hooks/use-campaign';
import type { UseChatReturn } from '@/routes/chat/chatTypes';
import { aiCoreApi } from '@/services/aiCoreClient';

export type CampaignPromptProps = {
  insert: UseChatReturn['insert'];
  className?: string;
  email?: string;
};

const contentVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { delay: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

const containerVariants = {
  expanded: {
    height: '300px',
  },
  minimized: {
    height: '46px',
  },
};

export function CampaignPrompt({
  insert,
  className,
  email,
}: CampaignPromptProps) {
  const { t } = useLingui();
  const {
    expanded,
    setExpanded,
    finishedRegistration,
    setFinishedRegistration,
  } = useCampaign();
  const { chatId } = useChatConfig();

  const { mutateAsync: resendEmail, isPending: isResendingEmail } = useMutation(
    {
      mutationFn: async () => {
        if (!email) {
          return;
        }

        await aiCoreApi.auth.resend({ email });
      },
    },
  );

  return (
    <div
      className={cn(
        'absolute left-0 top-14 z-50 w-full py-5 transition-[padding] duration-200 ease-out',
        expanded ? 'px-5' : 'px-14 hover:scale-105 transition-all',
        className,
      )}
    >
      <motion.div
        variants={containerVariants}
        initial={false}
        animate={expanded ? 'expanded' : 'minimized'}
        transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
        className='w-full overflow-hidden rounded-3xl border border-chat-border bg-chat-background shadow-sm drop-shadow-xl'
      >
        <div className='relative size-full'>
          <AnimatePresence>
            {expanded && (
              <motion.div
                key='expandedContent'
                variants={contentVariants}
                initial='initial'
                animate='animate'
                exit='exit'
                className='absolute inset-0'
              >
                <div className='relative flex flex-col gap-2 p-5 text-sm'>
                  <button
                    data-cta='chatbot-campaign-prompt-close-button'
                    className='absolute right-3 top-2 p-3 transition-all hover:scale-125 active:scale-95'
                    type='button'
                    onClick={() => setExpanded(false)}
                  >
                    <MinusIcon className='size-5' />
                  </button>
                  <div className='flex flex-col gap-2 p-4 text-chat-foreground'>
                    <h4 className='text-2xl font-bold'>
                      <Trans>
                        Hi there 👋
                        <br /> Do you like{' '}
                        <span className='opacity-60'>Kanbu</span>?
                      </Trans>
                    </h4>
                    <p className='text-sm'>
                      <Trans>Let us help you choose:</Trans>
                    </p>
                  </div>
                  <div className='flex flex-col gap-2'>
                    {finishedRegistration ? (
                      <Button
                        data-cta='chatbot-campaign-prompt-finished-button'
                        variant='ghost'
                        className='cursor-default touch-none hover:scale-100'
                        circle
                      >
                        <Trans>🎉 You&apos;re all set!</Trans>
                      </Button>
                    ) : (
                      <Button
                        data-cta='playground-widget-register'
                        variant='outline'
                        onClick={async () => {
                          try {
                            await resendEmail();

                            insert(
                              t`We've sent you an email to **complete your registration** at ${email}.\n\nPlease check your inbox and follow the instructions. 🙂`,
                              {
                                persist: true,
                              },
                            );

                            insert(
                              t`You're just a few steps away from finishing your AI agent! Now you just need to:\n\n* **Add data sources** (e.g., website links, documents, or XML feeds)\n\n* **Customize the behavior** and style of responses\n\n* **Configure the appearance** of the AI chatbot\n\n* Optionally **invite colleagues** for testing\n\n* **Upload to your website**\n\n 👉 Continue with the [short guide here](${AppSettings.docs.getStarted}).`,
                              {
                                persist: true,
                              },
                            );
                          } catch (error) {
                            console.error(error);

                            insert(
                              t`We're sorry, we're currently experiencing issues with our email service. But you should already have received an email to **complete your registration** at ${email}.\n\nPlease check your inbox and follow the instructions. 🙂`,
                              {
                                persist: true,
                              },
                            );
                          } finally {
                            setFinishedRegistration(true);
                          }
                        }}
                        loading={isResendingEmail}
                        disabled={isResendingEmail}
                        circle
                      >
                        <Trans>✅ Create chatbot without support</Trans>
                      </Button>
                    )}
                    <Button
                      data-cta='playground-widget-demo'
                      variant='outline'
                      circle
                    >
                      <a
                        target='_blank'
                        href='https://app.reclaim.ai/m/kanbu/demo'
                        rel='noreferrer'
                      >
                        <Trans>🗓️ Schedule a meeting</Trans>
                      </a>
                    </Button>
                    <Button
                      variant='outline'
                      data-cta='playground-widget-embed'
                      circle
                      onClick={() => {
                        insert(
                          t`To embed Kanbu on your website, simply copy the following code and paste it into your website's header! 🥳`,
                          {
                            persist: true,
                          },
                        );
                        insert(
                          `\`\`\`\n<script async src="https://loader.kanbu.ai/v0/embed.js" data-kanbu-agent-id="${chatId}">\n</script>\n\`\`\``,
                          {
                            persist: true,
                          },
                        );
                      }}
                    >
                      <Trans>💬 Embed on website</Trans>
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {!expanded && (
              <motion.div
                key='minimizedContent'
                variants={contentVariants}
                initial='initial'
                animate='animate'
                exit='exit'
                className='absolute inset-0 cursor-pointer'
                onClick={() => setExpanded(true)}
                onKeyDown={e =>
                  (e.key === 'Enter' || e.key === ' ') && setExpanded(true)
                }
                role='button'
                tabIndex={0}
              >
                <div className='flex h-full items-center justify-between px-5 text-sm'>
                  <p className='truncate text-chat-foreground'>
                    <Trans>
                      Hi there 👋 Do you like Kanbu? Let us help you choose...
                    </Trans>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
